import { useEffect, useRef } from 'react';
import useSessionState from './useSessionState';

// Auto-refresh, on by default (HIL-87), remembered when a user turns it off.
//
// WHY A SHARED HOOK RATHER THAN THREE COPIES. Three Sync screens poll — the merged Summary +
// Reconciliation card, the Process Run card, and the project's Extraction summaries — each with its own
// toggle, interval and effect. Defaulting them on individually would have left three places to get the
// pause behaviour right, and the one that was missed would keep polling in a background tab for as long
// as the browser was open.
//
// ON BY DEFAULT, BUT NOT INSISTENT. sessionStorage rather than useState, so turning it off stays off for
// the tab. A default that reasserts itself every time you navigate is not a default, it is a fight.
// Per-tab rather than permanent, matching useSessionState's reasoning: this is working state, and
// tomorrow's session should start from the helpful default again.
//
// PAUSED WHILE THE TAB IS HIDDEN. This is what makes on-by-default affordable: the reconciliation poll
// runs every 3s and reaches OpenSearch, so a forgotten tab is a query every three seconds, indefinitely,
// per user. Nobody reading a hidden tab needs it fresh. Polling resumes on the tab becoming visible, and
// fires ONCE immediately on return so the screen is current rather than up to an interval stale.
export default function useAutoRefresh(key, tick, intervalMs, { enabled = true } = {}) {
    const [on, setOn] = useSessionState(key, true);

    // The ref always points at the newest closure, so the interval sees current filters and state
    // without being torn down and rebuilt on every render.
    const tickRef = useRef(tick);
    tickRef.current = tick;

    useEffect(() => {
        if (!on || !enabled) return undefined;

        let timer = null;
        const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
        const start = () => {
            if (timer) return;
            timer = setInterval(() => { if (!document.hidden) tickRef.current(); }, intervalMs);
        };

        const onVisibility = () => {
            if (document.hidden) { stop(); return; }
            tickRef.current();   // catch up straight away, then resume the cadence
            start();
        };

        if (!document.hidden) start();
        document.addEventListener('visibilitychange', onVisibility);
        return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
    }, [on, enabled, intervalMs]);

    return [on, setOn];
}
